import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import { submitRequest } from './submitRequest';
import {
  ExportRedirectsParams,
  UploadRedirectsAsyncPayload,
  UploadRedirectsAsyncResponse,
  ImportJobDetails,
  RedirectsUsersMeType,
  GetRedirectsParams,
  PaginatedRedirectsResponse,
  DetailedRedirect,
  Redirect,
  RedirectPayload,
} from '@mercedes-benz/pmas-common';

const uint8ArrayToBase64 = (bytes: Uint8Array): string => {
  // Safe for our 1MB max payload; chunk to avoid call stack limits.
  const chunkSize = 0x8000;
  let binary = '';

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCodePoint(...chunk);
  }

  return btoa(binary);
};

export class RaasApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(discoveryApi: DiscoveryApi, fetchApi: FetchApi) {
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
  }
  async getMe(): Promise<RedirectsUsersMeType> {
    return submitRequest<RedirectsUsersMeType>({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: `/raas/users/me`,
    });
  }

  async uploadRedirectsAsync(
    payload: UploadRedirectsAsyncPayload,
  ): Promise<UploadRedirectsAsyncResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('pmas');

    const bytes =
      payload.buffer instanceof Uint8Array
        ? payload.buffer
        : new Uint8Array(payload.buffer);

    const contentBase64 = uint8ArrayToBase64(bytes);

    const response = await this.fetchApi.fetch(
      `${baseUrl}/raas/upload/redirects-async`,
      {
        method: 'POST',
        headers: {
          // The Backstage backend enforces JSON content-type globally.
          // We send base64 here; the PMAS backend plugin converts to octet-stream for the real RaaS v2 API.
          'content-type': 'application/json',
          'x-import-format': payload.importFormat,
        },
        body: JSON.stringify({
          filename: payload.filename,
          mimeType: payload.mimeType,
          importFormat: payload.importFormat,
          contentBase64,
        }),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return (await response.json()) as UploadRedirectsAsyncResponse;
  }

  async getImportJobDetails(importJobId: string): Promise<ImportJobDetails> {
    return submitRequest<ImportJobDetails>({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: `/raas/uploads/redirects/${encodeURIComponent(importJobId)}`,
    });
  }

  async getRedirects(
    params?: GetRedirectsParams,
    options?: { signal?: AbortSignal },
  ): Promise<PaginatedRedirectsResponse> {
    const qs = new URLSearchParams();

    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.pageSize !== undefined)
      qs.set('pageSize', String(params.pageSize));
    if (params?.sortField) qs.set('sortField', params.sortField);
    if (params?.sortDirection) qs.set('sortDirection', params.sortDirection);
    if (params?.originDomain) qs.set('originDomain', params.originDomain);
    if (params?.originPath) qs.set('originPath', params.originPath);
    if (params?.targetDomain) qs.set('targetDomain', params.targetDomain);
    if (params?.targetPath) qs.set('targetPath', params.targetPath);
    if (params?.statusCode !== undefined)
      qs.set('statusCode', String(params.statusCode));
    if (params?.desiredState) qs.set('desiredState', params.desiredState);
    if (params?.syncState) qs.set('syncState', params.syncState);

    const query = qs.toString();
    const path = query ? `/raas/redirects?${query}` : `/raas/redirects`;

    return submitRequest<PaginatedRedirectsResponse>({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path,
      signal: options?.signal,
    });
  }

  async getRedirect(redirectId: number): Promise<DetailedRedirect> {
    return submitRequest<DetailedRedirect>({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: `/raas/redirects/${redirectId}`,
    });
  }

  async createRedirect(redirect: RedirectPayload): Promise<Redirect> {
    return submitRequest<Redirect>({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'POST',
      path: `/raas/redirects`,
      body: {
        redirect: redirect,
      },
    });
  }

  async updateRedirect(
    redirectId: number,
    redirect: Partial<RedirectPayload>,
  ): Promise<Redirect> {
    return submitRequest<Redirect>({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'PUT',
      path: `/raas/redirects/${redirectId}`,
      body: {
        redirect: redirect,
      },
    });
  }

  async deleteRedirect(redirectId: number): Promise<unknown> {
    return submitRequest<unknown>({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'DELETE',
      path: `/raas/redirects/${redirectId}`,
    });
  }

  async deleteRedirectsBulk(ids: number[]): Promise<unknown> {
    return submitRequest<unknown>({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'POST',
      path: `/raas/redirects/bulk-delete`,
      body: { ids },
    });
  }

  async exportRedirects(params: ExportRedirectsParams): Promise<Response> {
    const qs = new URLSearchParams();

    qs.set('format', params.format);

    if (params.originDomain) {
      qs.set('originDomain', params.originDomain);
    }
    if (params.originPath) {
      qs.set('originPath', params.originPath);
    }
    if (params.targetDomain) {
      qs.set('targetDomain', params.targetDomain);
    }
    if (params.targetPath) {
      qs.set('targetPath', params.targetPath);
    }
    if (
      params.statusCode !== undefined &&
      params.statusCode !== null &&
      params.statusCode !== ''
    ) {
      qs.set('statusCode', String(params.statusCode));
    }

    return submitRequest<Response>({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: `/raas/download/redirects?${qs.toString()}`,
      responseType: 'response',
    });
  }

  async exportRedirectsTemplate(): Promise<Response> {
    return submitRequest<Response>({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: '/raas/download/redirects-template',
      responseType: 'response',
    });
  }
}
